import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getDeviseCourante } from '../../utils/format';

// Formate un nombre dans la devise du commerce
function formatGNF(n) {
  return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante();
}
function formatGNFCompact(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace('.0', '') + ' M';
  if (v >= 1_000)     return Math.round(v / 1_000) + ' k';
  return String(v);
}
// "2026-05-08" → "jeu. 8" (format court FR)
function formatDateLabel(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
}

// Tooltip personnalisé : fond sombre cohérent avec le thème
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  const date  = new Date(label).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long'
  });
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl text-sm">
      <p className="text-white font-semibold capitalize mb-2">{date}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-xs mb-1">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
          <span className="text-gray-400 capitalize">{p.name}</span>
          <span className="text-white font-medium ml-auto">{formatGNF(p.value)}</span>
        </p>
      ))}
      <div className="border-t border-gray-800 mt-2 pt-2 flex items-center justify-between text-xs">
        <span className="text-gray-400">Total</span>
        <span className="text-yellow-400 font-bold">{formatGNF(total)}</span>
      </div>
    </div>
  );
}

// Accepte soit `period={key,label,from,to}` (nouveau), soit `days={number}` (rétro-compat).
export default function EvolutionChart({ period, days = 7 }) {
  const { moduleActif } = useAuth();
  // Courbes des modules désactivés masquées (tableau de bord dynamique)
  const aTerrain = moduleActif('vendeurs_terrain');
  const aLigne   = moduleActif('commandes_ligne');
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  // Sérialiser les params pour la dépendance useEffect — évite les re-fetch
  // intempestifs causés par une nouvelle référence d'objet à chaque render.
  const paramsKey = period ? `from=${period.from}&to=${period.to}` : `days=${days}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    api.get(`/dashboard/evolution?${paramsKey}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [paramsKey]);

  const total = data.reduce((s, d) => s + (d.total || 0), 0);
  const moy   = data.length > 0 ? Math.round(total / data.length) : 0;
  const nbJours = data.length;
  const labelPeriode = period?.label || `${days} derniers jours`;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <TrendingUp size={16} className="text-yellow-400" />
            Évolution du chiffre d'affaires
          </h3>
          <p className="text-gray-500 text-xs mt-0.5">
            {labelPeriode.toLowerCase()} · moyenne {formatGNF(moy)} / jour
          </p>
        </div>
        {!loading && !error && total > 0 && (
          <div className="text-right">
            <p className="text-gray-500 text-xs">Total {nbJours} jour{nbJours > 1 ? 's' : ''}</p>
            <p className="text-yellow-400 font-bold text-base">{formatGNF(total)}</p>
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full" />
        </div>
      ) : error ? (
        <p className="text-gray-600 text-sm text-center py-12">
          Impossible de charger les données
        </p>
      ) : total === 0 ? (
        <p className="text-gray-600 text-sm text-center py-12">
          Aucune vente sur la période
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="gradMagasin" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#eab308" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#eab308" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="gradTerrain" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#3b82f6" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="gradLivraison" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#22c55e" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateLabel}
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              axisLine={{ stroke: '#374151' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={formatGNFCompact}
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={45}
            />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: '#374151', strokeDasharray: '3 3' }} />
            <Legend
              wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
              iconType="circle"
              formatter={(v) => <span className="text-gray-400 capitalize">{v}</span>}
            />
            <Area
              type="monotone" dataKey="magasin" name="Magasin" stackId="1"
              stroke="#eab308" strokeWidth={2} fill="url(#gradMagasin)"
            />
            {aTerrain && (
              <Area
                type="monotone" dataKey="terrain" name="Terrain" stackId="1"
                stroke="#3b82f6" strokeWidth={2} fill="url(#gradTerrain)"
              />
            )}
            {aLigne && (
              <Area
                type="monotone" dataKey="livraison" name="Vente en Ligne" stackId="1"
                stroke="#22c55e" strokeWidth={2} fill="url(#gradLivraison)"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
